import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { KrsEntry } from '../models/krs-entry/krs-entry.model';
import { NewUpdateSubskrypcjaContext } from '../models/new-update-subskrypcja-context.model';
import { ObjectResultMessage } from '../models/object-result-message.model';
import { SubskrypcjaNaglowek } from '../models/subskrypcja-naglowek.model';
import { WszystkieSubskrypcje } from '../models/wszystkie-subskrypcje.model';
import {SzczegolySubskrypcji} from "../models/szczegoly-subskrypcji.model";
import {Zdarzenie} from "../models/zdarzenie.model";
import { SubskrypcjaContext } from '../models/subskrypcja-context.model';
import { Konfiguracja } from '../models/konfiguracja.model';

@Injectable({
  providedIn: 'root'
})
export class EformsService {
  constructor(private http: HttpClient) { }

  getKrs(NumerPozycji: string): Observable<KrsEntry> {
    let podmioty: Observable<KrsEntry>[] = [
      this.getKrsCore(NumerPozycji, 'RP'),
      this.getKrsCore(NumerPozycji, 'RS'),
      this.pobierzDanePodmiotu(NumerPozycji)
    ];
    return forkJoin(podmioty).pipe(map((result: any[]) => {
      let krsEntry: KrsEntry = null;
      if (result && (result[0] || result[1])) {
        this.checkKrsResponse(result[0]);
        this.checkKrsResponse(result[1]);
        krsEntry = this.mergeRecords(result);
      } else if (result[2]) {
        this.checkKrsResponse(result[2]);
        krsEntry = this.mapWykreslonyPodmiot(result[2].danePodmiotu);
      }
      if (krsEntry) {
        krsEntry.numerKRS = NumerPozycji;
      }
      return krsEntry;
    }));
  }

  getKrsCore(numerPozycji: string, rodzajRejestru: string, url: string = '/api/forms/krs/entry'): Observable<KrsEntry> {
    return this.http.get<any>(`${environment.ekrsApiUrl}${url}`, { params: { NumerPozycji: numerPozycji, RodzajRejestru: rodzajRejestru } });
  }

  pobierzDanePodmiotu(NumerPozycji: string): Observable<any> {
    return this.http.get<any>(`${environment.ekrsApiUrl}/api/forms/krs/pobierzdanepodmiotu`, { params: { NumerPozycji } });
  }

  dodajSubskrypcje(daneSubskrypcji: NewUpdateSubskrypcjaContext): Observable<any> {
    return this.http.post(`${environment.ekrsApiUrl}/api/newsletter/subskrypcja`, daneSubskrypcji);
  }

  edytujSubskrypcje(id: string, daneSubskrypcji: NewUpdateSubskrypcjaContext, ): Observable<any> {
    return this.http.put(`${environment.ekrsApiUrl}/api/newsletter/subskrypcja/${id}`, daneSubskrypcji);
  }

  odwolajSubskrypcje(id: string): Observable<any> {
    return this.http.post(`${environment.ekrsApiUrl}/api/newsletter/subskrypcja/odwolaj/${id}`, null);
  }

  usunSubskrypcje(id: string): Observable<any> {
    return this.http.delete(`${environment.ekrsApiUrl}/api/newsletter/subskrypcja/${id}`);
  }

  pobierzDaneSubskrypcji(subskrypcjaId: string): Observable<SubskrypcjaContext> {
    return this.http.get<SubskrypcjaContext>(`${environment.ekrsApiUrl}/api/newsletter/subskrypcja/${subskrypcjaId}`);
  }

  pobierzSubskrypcje(): Observable<WszystkieSubskrypcje[]> {
    return this.http.get<WszystkieSubskrypcje[]>(`${environment.ekrsApiUrl}/api/newsletter/subskrypcja/uzytkownik`);
  }


  pobierzSzczegolySubskrypcji(subskrypcjaId: string): Observable<SzczegolySubskrypcji> {
    return this.http.get<SzczegolySubskrypcji>(`${environment.ekrsApiUrl}/api/newsletter/subskrypcja/szczegoly/${subskrypcjaId}`);
  }

  pobierzZdarzenaWczesniejsze(subskrypcjaId: string): Observable<Zdarzenie[]> {
    return this.http.get<Zdarzenie[]>(`${environment.ekrsApiUrl}/api/newsletter/subskrypcja/${subskrypcjaId}/zdarzenia/wczesniejsze`);
  }

  czyMoznaDodac(naglowek: SubskrypcjaNaglowek): Observable<any> {
    return this.http.post(`${environment.ekrsApiUrl}/api/newsletter/subskrypcja/czymoznadodac`, naglowek);
  }

  pobierzKonfiguracje(): Observable<Konfiguracja> {
    return this.http.get<Konfiguracja>(`${environment.ekrsApiUrl}/api/newsletter/dajkonfiguracje`);
  }

  private checkKrsResponse(responce: ObjectResultMessage<number>) {
    if (responce && responce.result <= -1) {
      throw Error(responce.message || 'Brak możliwości wyszukania podmiotu');
    }
  }

  private mergeRecords(result) {
    if (result.length && (result[0] || result[1])) {
      let podmiot: KrsEntry = <KrsEntry>{};
      result.slice().reverse().forEach(zapisWRejestrze => podmiot = { ...podmiot, ...zapisWRejestrze });

      /**
       * Trawersowanie obiektu KRS Entry. W odpisach z RS są informacje, których nie ma w RP i na odwrót
       * Jako bazowe entry wziąłem RP i w przypadku, gdy w RP jakieś pole jest nullem i jeśli coś znajduje się w tym miejscu w RS to nadpisuję dane drugim rejestrem.
       */
      if (result[0] && result[1]) {
        const temp: KrsEntry = { ...result[0] };
        this.traverseObject(result[0], result[1], temp);
        podmiot = { ...temp };
      }

      if (result[0]) {
        podmiot.naglowekA.zarejestrowanyWRP = true;
      }

      const opp = podmiot.dzial3.przedmiotDzialnosciOPP?.nieodplatnyPkd?.length > 0 || podmiot.dzial3.przedmiotDzialnosciOPP?.odplatyPkd?.length > 0;
      podmiot.naglowekA.czyJestOPP = opp;
      return podmiot;
    } else {
      return null;
    }
  }

  private traverseObject(wpisRP: any, wpisRS: any, entry: any) {
    Object.keys(wpisRP)
      .forEach(key => {
        if (wpisRP[key] && typeof wpisRP[key] === 'object') {
          this.traverseObject(wpisRP[key], wpisRS[key] ? wpisRS[key] : {}, entry[key]);
        }
        if (!wpisRP[key] && wpisRS[key]) {
          entry[key] = wpisRS[key];
        }
      });
  }

  private mapWykreslonyPodmiot(dane): KrsEntry {

    const wykreslony: boolean = (+dane.dataWykresleniaZRP >= +dane.dataWpisuDoRP && dane.dataWykresleniaZRS)
      || (+dane.dataWykresleniaZRS >= +dane.dataWpisuDoRS && dane.dataWykresleniaZRP)
      || (+dane.dataWykresleniaZRP >= +dane.dataWpisuDoRP && +dane.dataWykresleniaZRS >= +dane.dataWpisuDoRS)
      ? true : false;

    return {
      dzial1: {
        // @ts-ignore brakuje pola adres
        siedzibaIAdres: {
          siedziba: {
            gmina: dane.gmina,
            kraj: null,
            miejscowosc: dane.miejscowosc,
            powiat: dane.powiat,
            wojewodztwo: dane.wojewodztwo,
          }
        },
        // @ts-ignore brakuje pól nipRegon i czyProwadziDzialalnoscZInnymiPodmiotami
        danePodmiotu: {
          formaPrawna: dane.formaPrawna,
          nip: dane.nip,
          nazwa: dane.nazwa,
          regon: dane.regon,
        },
      },
      naglowekA: { dataRejestracjiWKRS: null, ostatniWpis: null, wykreslony }
    };
  }
}
